import React, { FC, useEffect, useMemo, useState, useRef } from 'react';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import TopBlock from './TopBlock';
import { RootStoreType } from 'src/types/store';
import { allColumns } from './constants';
import { TransportIcon } from 'src/components/SkladonIcon/Icons';
import TableSimple from 'src/components/TableSimple';
import { ExportTypeType, TableRefType } from 'src/types/table';
import { useCheckPermission } from 'src/utils/checkPermission';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { buildShipperServiceOptions } from 'src/utils/shipperServiceFilter';
import { getTransportsListFetch } from 'src/resources/transportsList/reducer';

const TransportsListContainer: FC = () => {
    const dispatch = useDispatch();
    const tableRef = useRef<TableRefType>();

    const loading = useSelector((state: RootStoreType) => state.transportsList.loading);
    const transportsData = useSelector((state: RootStoreType) => state.transportsList.data);
    const shipperCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.shipperCodes);
    const serviceCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.serviceCodes);
    const transportStatusCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.transportStatusCodes);
    const transportTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.transportTypeCodes);

    const shipperServiceCodes = useMemo(
        () => buildShipperServiceOptions(serviceCodes, shipperCodes),
        [serviceCodes, shipperCodes]
    );

    const permissionCreate = useCheckPermission('TRANSPORT.CREATE');
    const permissionDetail = useCheckPermission('TRANSPORT.DETAIL');
    const permissionUpdate = useCheckPermission('TRANSPORT.UPDATE');

    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('transports.title') }));
        dispatch(getTransportsListFetch());
    }, []);

    const reloadTransportsList = () => {
        dispatch(getTransportsListFetch());
    };

    const handleExport = (type: ExportTypeType) => {
        tableRef.current?.exportTable(type, translate('transports.action.exportFileName'));
    };

    return (
        <>
            <TopBlock
                reloadTransportsList={reloadTransportsList}
                permissionCreate={permissionCreate}
                permissionUpdate={permissionUpdate}
                handleExport={handleExport}
            />
            <TableSimple
                tableRef={tableRef}
                rowIdKey="ID"
                rows={transportsData || []}
                loading={loading}
                columsLabelPrefix="transports.table"
                allColumns={allColumns}
                initSort={{ column: 'CREATED_AT', direction: 'DESC' }}
                rowActions={[
                    {
                        icon: <TransportIcon />,
                        label: translate('transports.action.transportDetail'),
                        getLink: (row) => `/transports/${row.ID}/`,
                        hiddenFce: () => !permissionDetail,
                    },
                ]}
                selectOptions={{
                    shipperCodes,
                    shipperServiceCodes,
                    transportStatusCodes,
                    transportTypeCodes,
                }}
                fullHeight
                optionsSessionName="transportsTable"
            />
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={(): any => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default TransportsListContainer;
